import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
  COMPANY_FLAG_FEATURES,
  COMPANY_FLAG_ROLES,
  CompanyEntity,
  CompanyEventCommonMeasureEntity,
  CompanyEventEntity,
  DigestEntity,
  DigestScheduleComposite,
  DigestScheduleEntity,
  DigestScheduleTemplateEntity,
  DigestSubscriptionEntity,
  EventEntity,
  PartialDigestScheduleComposite,
} from '@nk/data-entities-lib';

import { HttpParams } from '@angular/common/http';
import {
  AnalysisApiClientAccess,
  AppFeatureService,
  ApplicationApiClientAccess,
  AppRoleService,
} from '@nk/access-block';
import { DigestUnsubscribeMode } from '@nk/app/digest-unsubscribe/digest-unsubscribe.utils';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  AppRoleKey,
  CompanyEventCommonAnalysis,
  CompanyFlagHistoryCompositeEntity,
  COUNTRY_FEATURES,
  DigestPyramidEntity,
  SensitiveDataTypeKey,
} from '@nk/data-entities-lib';
import { arrayToDictList } from '@nk/utils-lib';
import { combineLatest, forkJoin, Observable, Subject } from 'rxjs';
import { filter, first, map, mergeMap, startWith, switchMap } from 'rxjs/operators';
import { RiskService } from './risk.service';
import { SENSITIVE_DATA_API_SERVICE_TOKEN, SensitiveDataApiService } from '@nk/api-lib';

export enum TemplateTypes {
  risk_interesting = 'risk_interesting',
  sales_interesting = 'sales_interesting',
}

@Injectable()
export class DigestService implements OnDestroy {
  private _digestScheduleUpdateByWatchlist = new Subject<string>();
  public digestScheduleUpdateByWatchlist: Observable<string> = this._digestScheduleUpdateByWatchlist.asObservable();

  constructor(
    private _applicationApiAccess: ApplicationApiClientAccess,
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _riskService: RiskService,
    private _cachingService: CachingService,
    private _appRoleService: AppRoleService,
    private _appFeatureService: AppFeatureService,
    @Inject(SENSITIVE_DATA_API_SERVICE_TOKEN) private _sensitiveDataService: SensitiveDataApiService,
  ) {}

  public digestScheduleOnWatchlistUpdated(watchlistUuid: string): void {
    this._digestScheduleUpdateByWatchlist.next(watchlistUuid);
  }

  public digestScheduleCompositeByWatchlist(
    // Will emit as digest schedule updates. For instance if it is deleted.
    watchlistUuid: string,
    appCustomerUuid: string,
  ): Observable<DigestScheduleComposite[]> {
    // We fetch digest schedule, then we listen to "digestScheduleUpdateByWatchlist", filtered on specific watchlist. And if any changes we will give the updated value.
    return combineLatest([
      this.fetchDigestScheduleComposite(appCustomerUuid, watchlistUuid).pipe(),
      this.digestScheduleUpdateByWatchlist.pipe(
        filter((a) => {
          return a === watchlistUuid;
        }),
        switchMap((watchlistUuid) => {
          return this.fetchDigestScheduleComposite(appCustomerUuid, watchlistUuid);
        }),
        startWith(null), // null is initial. Need initial value because of combineLatest.
      ),
    ]).pipe(
      filter(([initial, updatedValue]) => !!initial || updatedValue !== null), // should be either initial value or updatedValue (which is not null)
      map(([initial, updatedValue]) => {
        if (updatedValue !== null) {
          // null is just to make combinelatest run.
          return updatedValue;
        } else if (initial) {
          return initial;
        } else {
          console.warn('Should not reach here');
        }
      }),
    );
  }

  public postDigestScheduleComposite(
    payload: PartialDigestScheduleComposite,
    appCustomerIdentifier: string,
  ): Observable<DigestScheduleComposite> {
    return this._applicationApiAccess.post<DigestScheduleComposite>(
      `/app-customer/${appCustomerIdentifier}/digest-schedule-composite`,
      payload,
    );
  }

  public putDigestScheduleComposite(
    payload: PartialDigestScheduleComposite,
    digestScheduleUuid: string,
  ): Observable<DigestScheduleComposite> {
    return this._applicationApiAccess.put<DigestScheduleComposite>(
      `/digest-schedule/${digestScheduleUuid}/digest-schedule-composite`,
      payload,
    );
  }
  public fetchDigestScheduleComposite(
    appCustomerIdentifier: string,
    watchlistUuid?: string,
    digestScheduleUuid?: string,
  ): Observable<DigestScheduleComposite[]> {
    let httpParams = new HttpParams();
    if (watchlistUuid) {
      httpParams = httpParams.append('watchlist_uuid', watchlistUuid);
    }
    if (digestScheduleUuid) {
      httpParams = httpParams.append('digest_schedule_uuid', digestScheduleUuid);
    }
    return this._applicationApiAccess.get<DigestScheduleComposite[]>(
      `/app-customer/${appCustomerIdentifier}/digest-schedule-composite`,
      { params: httpParams },
    );
  }

  public digestSubscriptionByAppMembershipCache = this._cachingService.create(
    this,
    this.fetchDigestSubscriptionByAppUser,
  );
  public fetchDigestSubscriptionByAppUser(appMembershipUuid: string): Observable<DigestSubscriptionEntity[]> {
    return this._applicationApiAccess.get<DigestSubscriptionEntity[]>(
      `app-membership/${appMembershipUuid}/digest-subscription`,
    );
  }

  public digestSchedulesByCustomerCache = this._cachingService.create(this, this.fetchDigestSchedulesByCustomer);
  public fetchDigestSchedulesByCustomer(customerUuid: string): Observable<DigestScheduleEntity[]> {
    return this._applicationApiAccess.get<DigestScheduleEntity[]>(`app-customer/${customerUuid}/digest-schedule`);
  }

  public digestScheduleTemplateListCache = this._cachingService.create(this, this.fetchDigestScheduleTemplateList);
  public fetchDigestScheduleTemplateList(): Observable<DigestScheduleTemplateEntity[]> {
    return this._applicationApiAccess.get<DigestScheduleTemplateEntity[]>('digest-schedule-template');
  }

  public deleteDigestSubscription(
    digestSubscriptionUuid: string,
    digestUnsubscribeMode: DigestUnsubscribeMode = DigestUnsubscribeMode.Digest,
  ): Observable<DigestSubscriptionEntity> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('digest_unsubscribe_mode', digestUnsubscribeMode);
    return this._applicationApiAccess.delete<DigestSubscriptionEntity>(
      `digest-subscription/${digestSubscriptionUuid}`,
      { params: httpParams },
    );
  }

  public digestListByAppCustomerCache = this._cachingService.create(this, this.fetchDigestListByCustomer);
  public fetchDigestListByCustomer(customerUuid: string): Observable<DigestEntity[]> {
    return this._applicationApiAccess.get<DigestEntity[]>('app-customer/' + customerUuid + '/digest');
  }

  public digestListByWatchlistCache = this._cachingService.create(this, this.fetchDigestListByWatchlist);
  public fetchDigestListByWatchlist(watchlistUuid: string): Observable<DigestEntity[]> {
    return this._applicationApiAccess.get<DigestEntity[]>('watchlist/' + watchlistUuid + '/digest');
  }

  public digestListByDigestScheduleCache = this._cachingService.create(this, this.fetchDigestListByDigestScheduleUuid);
  public fetchDigestListByDigestScheduleUuid(digestScheduleUuid: string, limit?: number): Observable<DigestEntity[]> {
    let params = new HttpParams();
    if (limit) {
      params = params.set('limit', limit);
    }
    return this._applicationApiAccess.get<DigestEntity[]>('digest-schedule/' + digestScheduleUuid + '/digest', {
      params,
    });
  }

  public digestListByAppMembershipDigestSubscriptionCache = this._cachingService.create(
    this,
    this.fetchDigestListByAppMembershipDigestSubscription,
  );
  public fetchDigestListByAppMembershipDigestSubscription(
    appMembershipUuid: string,
    limit: number,
  ): Observable<DigestEntity[]> {
    let httpParams = new HttpParams();
    if (limit) {
      httpParams = httpParams.append('limit', String(limit));
    }
    return this._applicationApiAccess.get<DigestEntity[]>(
      'app-membership/' + appMembershipUuid + '/digest-subscription/digest',
      { params: httpParams },
    );
  }

  public digestCache = this._cachingService.create(this, this.fetchDigest);
  public fetchDigest(digestUuid: string): Observable<DigestEntity> {
    return this._applicationApiAccess.get<DigestEntity>('digest/' + digestUuid);
  }

  public digestPyramidCache = this._cachingService.create(this, this.fetchDigestPyramid);
  public fetchDigestPyramid(
    digestUuid: string,
    limit = 10,
    offset = 0,
    disableFiltering = false,
    watchlistUuid: string,
  ): Observable<DigestPyramidEntity> {
    // Limit is related to number of top level companies/corporate groups.
    let httpParams = new HttpParams();
    if (offset) {
      httpParams = httpParams.append('offset', String(offset));
    }
    if (limit) {
      httpParams = httpParams.append('limit', String(limit));
    }
    if (disableFiltering) {
      httpParams = httpParams.append('disable_filtering', String(disableFiltering));
    }

    return this._sensitiveDataService
      .claimValidSensitiveDataTypeKeysByWatchlist(watchlistUuid, [SensitiveDataTypeKey.payment_remark])
      .pipe(
        switchMap((validSensitiveDataTypeKeys) => {
          return this._applicationApiAccess.get<DigestPyramidEntity>(
            'digest/' + digestUuid + '/digest-pyramid',
            { params: httpParams },
            [
              AppFeatureKey.base,
              AppFeatureKey.events_news,
              AppFeatureKey.payment_remark,
              AppFeatureKey.company_risk_score,
              ...COUNTRY_FEATURES,
            ],
            [AppRoleKey.credit_check],
            validSensitiveDataTypeKeys,
          );
        }),
      );
  }

  public companyListByDigestCache = this._cachingService.create(this, this.fetchCompanyListByDigest);
  public fetchCompanyListByDigest(digestUuid: string): Observable<CompanyEntity[]> {
    return this._applicationApiAccess.get<CompanyEntity[]>(`digest/${digestUuid}/company`);
  }

  public eventListByDigestCache = this._cachingService.create(this, this.fetchEventListByDigest);
  public fetchEventListByDigest(digestUuid: string): Observable<EventEntity[]> {
    return this._applicationApiAccess.get<EventEntity[]>('digest/' + digestUuid + '/event', {}, [
      AppFeatureKey.base,
      AppFeatureKey.company_risk_score,
    ]);
  }

  public companyEventListByDigestCache = this._cachingService.create(this, this.fetchCompanyEventListByDigest);
  public fetchCompanyEventListByDigest(digestUuid: string): Observable<CompanyEventEntity[]> {
    return this._applicationApiAccess.get<CompanyEventEntity[]>('digest/' + digestUuid + '/company-event', {}, [
      AppFeatureKey.base,
      AppFeatureKey.events_news,
      AppFeatureKey.company_risk_score,
    ]);
  }

  public companyEventCommonMeasureListByDigestCache = this._cachingService.create(
    this,
    this.fetchCompanyEventCommonMeasureListByDigest,
  );
  public fetchCompanyEventCommonMeasureListByDigest(digestUuid: string): Observable<CompanyEventCommonMeasureEntity[]> {
    return this._applicationApiAccess.get<CompanyEventCommonMeasureEntity[]>(
      'digest/' + digestUuid + '/company-event-common-measure',
      {},
      [AppFeatureKey.base, AppFeatureKey.company_risk_score],
    );
  }

  public digestByCompanyEventCache = this._cachingService.create(this, this.fetchDigestByCompanyEvent);
  public fetchDigestByCompanyEvent(uuid: string): Observable<DigestEntity[]> {
    return this._applicationApiAccess.get<DigestEntity[]>('company-event/' + uuid + '/digest');
  }

  public companyFlagHistoryByDigestCache = this._cachingService.create(this, this.fetchCompanyFlagHistoryByDigest);
  public fetchCompanyFlagHistoryByDigest(
    digestUuid: string,
    snapshotDate: Date = null,
    fromFlagDate: Date = null,
  ): Observable<CompanyFlagHistoryCompositeEntity[]> {
    let httpParams = new HttpParams();
    if (fromFlagDate) {
      httpParams = httpParams.append('from_flag_date', fromFlagDate.toISOString());
    } else if (snapshotDate) {
      httpParams = httpParams.append('snapshot_date', snapshotDate.toISOString());
    }
    return this._analysisApiAccess.get<CompanyFlagHistoryCompositeEntity[]>(
      `digest/${digestUuid}/company-flag-history-composite`,
      { params: httpParams },
      [AppFeatureKey.base, ...COMPANY_FLAG_FEATURES, ...COUNTRY_FEATURES],
      [...COMPANY_FLAG_ROLES],
    );
  }

  public deleteDigestSchedule(digestScheduleUuid: string): Observable<DigestScheduleEntity> {
    return this._applicationApiAccess.delete<DigestScheduleEntity>(`digest-schedule/${digestScheduleUuid}`);
  }

  public deleteDigest(digestUuid: string): Observable<DigestEntity> {
    return this._applicationApiAccess.delete<DigestEntity>(`digest/${digestUuid}`);
  }

  public createDigest(fromTimestamp: Date, toTimestamp: Date, watchlistUuid: string): Observable<DigestEntity> {
    const payload = {
      from_timestamp: fromTimestamp.toISOString(),
      to_timestamp: toTimestamp.toISOString(),
      watchlist_uuid: watchlistUuid,
    };
    return this._applicationApiAccess.post<DigestEntity>('digest', payload);
  }

  public updateDigest(digestUuid: string, payload: any): Observable<DigestEntity> {
    return this._applicationApiAccess.patch<DigestEntity>('digest/' + digestUuid, payload);
  }

  public ngOnDestroy(): void {}

  public digestScheduleCache = this._cachingService.create(this, this.fetchDigestSchedule);
  public fetchDigestSchedule(digestScheduleUuid: string): Observable<DigestScheduleEntity> {
    return this._applicationApiAccess.get<DigestScheduleEntity>('digest-schedule/' + digestScheduleUuid);
  }

  public digestHierarchyCache = this._cachingService.create(this, this.fetchDigestHierarchy);
  public fetchDigestHierarchy(digestUuid: string): Observable<DigestHierarchy> {
    const digestFactory = new DigestFactory(digestUuid, this, this._riskService);
    return digestFactory.fetch();
  }

  public resolveApplicableAnalysis(analyses: CompanyEventCommonAnalysis[]): CompanyEventCommonAnalysis {
    if (analyses && analyses.length > 0) {
      analyses.sort((a1, a2) => a1.priority.model_rank - a2.priority.model_rank);
      return analyses[0];
    } else {
      return null;
    }
  }
}

export class DigestFactory {
  public digest: DigestEntity;
  public digestCompanyList: CompanyEntity[];
  public digestCompanyEventList: CompanyEventEntity[];
  public digestCompanyEventCommonMeasureList: CompanyEventCommonMeasureEntity[];
  public digestCompanyEventCommonAnalysisList: CompanyEventCommonAnalysis[];
  public digestEventList: EventEntity[];

  constructor(
    private _digestUuid: string,
    private _digestService: DigestService,
    private _riskService: RiskService,
  ) {}

  public fetch(): Observable<DigestHierarchy> {
    return forkJoin(
      this._digestService.digestCache.get(this._digestUuid).pipe(first()),
      this._digestService.companyListByDigestCache.get(this._digestUuid).pipe(first()),
      this._digestService.companyEventListByDigestCache.get(this._digestUuid).pipe(first()),
      this._digestService.eventListByDigestCache.get(this._digestUuid).pipe(first()),
      this._digestService.companyEventCommonMeasureListByDigestCache.get(this._digestUuid).pipe(first()),
    ).pipe(
      mergeMap((payloads) => {
        [
          this.digest,
          this.digestCompanyList,
          this.digestCompanyEventList,
          this.digestEventList,
          this.digestCompanyEventCommonMeasureList,
        ] = payloads;
        return this._riskService.resolveCompanyEventCommonAnalysisList(this.digestCompanyEventCommonMeasureList);
      }),
      map((payload) => {
        this.digestCompanyEventCommonAnalysisList = payload;

        const company2match = arrayToDictList(this.digestCompanyEventList, (m) => m.company_uuid);

        return new DigestHierarchy(
          this.digest,
          this.digestCompanyList.map((company) => {
            const companyEventList = company2match[company.uuid] || [];
            const digestCompanyEventList = companyEventList.map(
              (match) =>
                new DigestCompanyEvent(
                  match,
                  this.digestEventList.find((event) => match.event_uuid === event.uuid),
                  this.digestCompanyEventCommonAnalysisList.filter(
                    (measure) => measure.measure.company_event_uuid === match.uuid,
                  ),
                ),
            );
            return new DigestCompany(company, digestCompanyEventList);
          }),
        );
      }),
    );
  }
}

export class DigestCompanyEvent {
  constructor(
    public companyEvent: CompanyEventEntity,
    public event: EventEntity,
    public companyEventCommonAnalysisList: CompanyEventCommonAnalysis[],
  ) {}
}

export class DigestCompany {
  constructor(
    public company: CompanyEntity,
    public digestCompanyEventList: DigestCompanyEvent[],
  ) {}
}

export class DigestHierarchy {
  constructor(
    public digest: DigestEntity,
    public digestCompanyList: DigestCompany[],
  ) {}
}
